import {HttpErrorResponse} from '@angular/common/http';
import {NgTemplateOutlet} from '@angular/common';
import {Component, ElementRef, Input, OnInit, computed, effect, inject, signal} from '@angular/core';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonCheckbox,
  IonIcon,
  IonSpinner,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import {TranslocoModule, TranslocoService, provideTranslocoScope} from '@jsverse/transloco';

import {
  DOCUMENTO_VERSIONE_NON_CORRENTE_KEY,
  DocumentiComuneApi,
  DocumentiDaAccettare,
  DocumentoCittadino,
  DocumentoVigente,
} from '../../../services/documenti-comune/documenti-comune.api';
import {parseBackendLocalDateTime} from '../../utils/backend-datetime.util';
import {TerminiTestoComponent} from '../../components/termini-testo/termini-testo.component';

/** Ruoli con cui la modale si chiude (`canDismiss` del gate li distingue da backdrop e gesture). */
export const DOCUMENTI_COMUNE_ESITO_ACCETTATO = 'accettato';
export const DOCUMENTI_COMUNE_ESITO_ESCI = 'esci';
export const DOCUMENTI_COMUNE_ESITO_SISTEMA = 'sistema';
export const DOCUMENTI_COMUNE_ESITO_CHIUDI = 'chiudi';

export type DocumentiComuneModalita = 'gate' | 'consulta';

/** Errore mostrato sopra i pulsanti del gate (una sola regione `role="alert"`). */
export type DocumentiComuneErrore = 'obbligatorie' | 'versione' | 'generico' | null;

/** Stato della lettura in modalità consulta. */
export type DocumentiComuneConsultaStato = 'caricamento' | 'pronto' | 'errore';

/**
 * Modale dei documenti del Comune, in due modalità.
 *
 * <b>gate</b>: bloccante, elenco compatto su una schermata. Una card per documento (titolo, «Nuovo»
 * o «Aggiornato il», «Leggi il documento», spunte obbligatorie) e in fondo i consensi facoltativi di
 * tutti i documenti. Il testo integrale si apre in una vista interna con «Indietro» e il focus
 * torna al pulsante. Spunte mai preselezionate. Al cittadino non si mostra mai il numero di versione. «Accetta e continua» non è mai
 * disabilitato per validità: se mancano spunte obbligatorie mostra un messaggio `role="alert"` e
 * porta il focus sulla prima mancante (WCAG 3.3.1, 3.3.3), invece di lasciare un pulsante inerte
 * senza spiegazione. «Esci» è sempre visibile e chiude la modale con l'esito `esci`: il logout lo
 * esegue chi l'ha aperta, a modale già chiusa.
 *
 * <b>consulta</b>: sola lettura (dal profilo), con data e valori dell'ultima accettazione.
 */
@Component({
  selector: 'app-documenti-comune-modal',
  templateUrl: './documenti-comune-modal.component.html',
  styleUrls: ['./documenti-comune-modal.component.scss'],
  imports: [
    IonButton,
    IonButtons,
    IonCheckbox,
    IonContent,
    IonIcon,
    IonSpinner,
    IonTitle,
    IonToolbar,
    TranslocoModule,
    TerminiTestoComponent,
    NgTemplateOutlet,
  ],
  providers: [provideTranslocoScope({scope: 'documenti-comune', alias: 'documentiComune'})],
})
export class DocumentiComuneModalComponent implements OnInit {
  @Input() modalita: DocumentiComuneModalita = 'gate';
  @Input() slug = '';
  /**
   * Id con cui la modale è stata creata: la chiusura passa da qui, così una modale presentata sopra
   * (es. una push) non viene chiusa al posto di questa. Assente: si chiude quella in cima.
   */
  @Input() idModale?: string;
  /** Gate: documenti da accettare, già letti dal chiamante. */
  @Input() set documenti(valore: DocumentoCittadino[] | null | undefined) {
    this.documentiGate.set(valore ?? []);
  }

  /** Letti da `AutocloseOverlaysService` dalle `componentProps` della modale. */
  @Input() isNotDismissableWithBack = false;
  @Input() isAutoSkipSafe = false;

  readonly documentiGate = signal<DocumentoCittadino[]>([]);
  /** Valore di ogni spunta, per id. Parte vuoto: nessuna spunta è preselezionata. */
  readonly valori = signal<Record<string, boolean>>({});
  readonly errore = signal<DocumentiComuneErrore>(null);
  readonly invioInCorso = signal(false);
  /** Id delle spunte obbligatorie lasciate vuote dopo l'ultimo tentativo (per `aria-invalid`). */
  readonly mancanti = signal<ReadonlySet<string>>(new Set());

  readonly consultaStato = signal<DocumentiComuneConsultaStato>('caricamento');
  readonly vigenti = signal<DocumentoVigente[]>([]);
  /** Testo della regione `role="status"` della consulta: parte vuoto e si riempie dopo il primo render. */
  readonly annuncioConsulta = signal('');

  /** Gate: documento il cui testo integrale è aperto nella vista a tutto schermo (`null` = elenco). */
  readonly inLettura = signal<DocumentoCittadino | null>(null);

  /** Spunte facoltative (ACCONSENTO) di tutti i documenti, raggruppate in fondo all'elenco. */
  readonly facoltative = computed(() =>
    this.documentiGate().flatMap((d) => d.spunte.filter((s) => !s.obbligatoria).map((spunta) => ({spunta, doc: d}))),
  );

  /** Almeno un documento è un aggiornamento di uno già accettato: cambia il testo introduttivo. */
  readonly eAggiornamento = computed(() => this.documentiGate().some((d) => d.aggiornamento));

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor(
    private readonly modalController: ModalController,
    private readonly api: DocumentiComuneApi,
    private readonly transloco: TranslocoService,
  ) {
    // Il testo dei documenti è HTML del backend: le tabelle larghe scorrono in orizzontale e,
    // per chi usa la tastiera, devono poter ricevere il focus (WCAG 2.1.1).
    effect(() => {
      this.documentiGate();
      this.vigenti();
      this.inLettura();
      setTimeout(() => this.rendiTabelleScorrevoliFocalizzabili());
    });
  }

  ngOnInit(): void {
    if (this.modalita === 'consulta') {
      this.caricaConsulta();
    }
  }

  // ---- gate ---------------------------------------------------------------------------------

  impostaValore(spuntaId: string, valore: boolean): void {
    this.valori.update((v) => ({...v, [spuntaId]: valore}));
    if (this.mancanti().has(spuntaId) && valore) {
      this.mancanti.update((m) => new Set([...m].filter((id) => id !== spuntaId)));
      // Corrette tutte le spunte mancanti il messaggio non ha più ragione di restare.
      if (this.mancanti().size === 0 && this.errore() === 'obbligatorie') {
        this.errore.set(null);
      }
    }
  }

  valoreSpunta(spuntaId: string): boolean {
    return this.valori()[spuntaId] === true;
  }

  /** Id delle spunte obbligatorie non spuntate, nell'ordine in cui compaiono a schermo. */
  spunteObbligatorieMancanti(): string[] {
    const valori = this.valori();
    return this.documentiGate()
      .flatMap((d) => d.spunte)
      .filter((s) => s.obbligatoria && valori[s.id] !== true)
      .map((s) => s.id);
  }

  accetta(): void {
    if (this.invioInCorso()) {
      return;
    }
    const mancanti = this.spunteObbligatorieMancanti();
    if (mancanti.length > 0) {
      this.mancanti.set(new Set(mancanti));
      this.errore.set('obbligatorie');
      this.focalizzaSpunta(mancanti[0]);
      return;
    }

    this.errore.set(null);
    this.invioInCorso.set(true);
    const valori = this.valori();
    this.api
      .accetta$(this.slug, {
        documenti: this.documentiGate().map((d) => ({
          documento_id: d.documento_id,
          versione: d.versione,
          // Ogni spunta con un valore esplicito, anche `false` per le facoltative lasciate vuote.
          spunte: d.spunte.map((s) => ({spunta_id: s.id, valore: valori[s.id] === true})),
        })),
      })
      .subscribe({
        next: (stato) => this.dopoAccettazione(stato),
        error: (err: unknown) => this.gestisciErroreAccettazione(err),
      });
  }

  /** Apre il testo integrale del documento nella vista a tutto schermo e porta il focus sul suo titolo. */
  apriLettura(doc: DocumentoCittadino): void {
    this.inLettura.set(doc);
    this.focalizzaElemento('documenti-comune-lettura-titolo');
  }

  /** Torna all'elenco e restituisce il focus al pulsante «Leggi il documento» che l'aveva aperto. */
  chiudiLettura(): void {
    const doc = this.inLettura();
    this.inLettura.set(null);
    if (doc) {
      this.focalizzaElemento(this.idLeggi(doc.documento_id));
    }
  }

  /**
   * L'elemento (pulsante, titolo, introduzione) è ricreato dalla vista e `ion-content` idrata in modo
   * asincrono: finché lo slot non è reso non è focalizzabile, quindi si riprova brevemente fino a che
   * il focus c'è.
   */
  private focalizzaElemento(id: string, tentativi = 20): void {
    setTimeout(() => {
      const host = document.getElementById(id);
      if (host) {
        this.bottoneNativo(host).focus();
      }
      if (document.activeElement !== host && tentativi > 1) {
        this.focalizzaElemento(id, tentativi - 1);
      }
    }, 25);
  }

  /** Il button nativo dentro `ion-button` (shadow DOM o, senza shadow, light DOM); l'host come ripiego. */
  private bottoneNativo(host: HTMLElement): HTMLElement {
    return host.shadowRoot?.querySelector<HTMLElement>('button') ?? host.querySelector<HTMLElement>('button') ?? host;
  }

  idLeggi(documentoId: string): string {
    return `leggi-${documentoId}`;
  }

  /** Spunte obbligatorie del documento (le facoltative stanno nella sezione in fondo). */
  obbligatorie(doc: DocumentoCittadino) {
    return doc.spunte.filter((s) => s.obbligatoria);
  }

  esci(): void {
    void this.modalController.dismiss(null, DOCUMENTI_COMUNE_ESITO_ESCI, this.idModale);
  }

  /**
   * 422 «versione non corrente»: nel frattempo un documento è cambiato. Si rilegge lo stato e si
   * azzerano le spunte, perché valevano per un testo diverso. Ogni altro errore: messaggio generico
   * e la stessa azione resta disponibile come «Riprova».
   */
  private gestisciErroreAccettazione(err: unknown): void {
    const http = err as HttpErrorResponse;
    if (http?.status === 422 && http?.error?.key === DOCUMENTO_VERSIONE_NON_CORRENTE_KEY) {
      this.api.daAccettare$(this.slug).subscribe({
        next: (stato) => this.dopoAccettazione(stato),
        error: () => {
          this.invioInCorso.set(false);
          this.errore.set('generico');
        },
      });
      return;
    }
    this.invioInCorso.set(false);
    this.errore.set('generico');
  }

  /**
   * Con nulla più da accettare si chiude. Se invece ne restano (un documento pubblicato nel
   * frattempo, o una versione cambiata) si mostrano quelli, con le spunte azzerate.
   */
  private dopoAccettazione(stato: DocumentiDaAccettare): void {
    this.invioInCorso.set(false);
    if (!stato?.da_accettare || !stato.documenti?.length) {
      void this.modalController.dismiss(null, DOCUMENTI_COMUNE_ESITO_ACCETTATO, this.idModale);
      return;
    }
    this.inLettura.set(null);
    this.documentiGate.set(stato.documenti);
    this.valori.set({});
    this.mancanti.set(new Set());
    this.errore.set('versione');
    this.focalizzaElemento('documenti-comune-intro');
  }

  /** `ion-checkbox` è esso stesso l'elemento `role="checkbox"` focalizzabile: `setFocus()` lo raggiunge. */
  private focalizzaSpunta(spuntaId: string): void {
    setTimeout(() => {
      const el = document.getElementById(this.idSpunta(spuntaId)) as (HTMLElement & {setFocus?: () => Promise<void>}) | null;
      if (el?.setFocus) {
        void el.setFocus();
      } else {
        el?.focus();
      }
    });
  }

  /** Avvolge ogni tabella del documento in una regione scorrevole con nome, raggiungibile da tastiera. */
  private rendiTabelleScorrevoliFocalizzabili(): void {
    const tabelle = this.host.nativeElement.querySelectorAll<HTMLTableElement>('.documento table');
    tabelle.forEach((tabella) => {
      if (tabella.parentElement?.classList.contains('tabella-scorrevole')) {
        return;
      }
      const sezione = tabella.closest('section.documento');
      const titolo = sezione?.querySelector('.documento-titolo')?.textContent?.trim();
      // Più tabelle nello stesso documento: ognuna ha un nome distinto («<titolo> — tabella 2 di 3»).
      const dellaSezione = sezione ? Array.from(sezione.querySelectorAll('table')) : [];
      const nome =
        titolo && dellaSezione.length > 1
          ? this.transloco.translate('documentiComune.tabellaNome', {
              titolo,
              n: dellaSezione.indexOf(tabella) + 1,
              totale: dellaSezione.length,
            })
          : titolo;
      const contenitore = document.createElement('div');
      contenitore.className = 'tabella-scorrevole';
      contenitore.tabIndex = 0;
      contenitore.setAttribute('role', 'region');
      contenitore.setAttribute('lang', 'it');
      if (nome) {
        contenitore.setAttribute('aria-label', nome);
      }
      tabella.parentElement?.insertBefore(contenitore, tabella);
      contenitore.appendChild(tabella);
    });
  }

  /**
   * Data e ora nel formato della lingua attiva. I timestamp del backend sono `LocalDateTime` UTC
   * senza zona: si interpretano come UTC e si mostrano nel fuso del device. Il valore grezzo se non
   * è una data valida.
   */
  dataAccettazione(iso: string): string {
    const d = parseBackendLocalDateTime(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleString(this.transloco.getActiveLang() || 'it', {dateStyle: 'short', timeStyle: 'short'});
  }

  /** Solo la data (giorno esteso), stessa interpretazione UTC. Stringa vuota se assente o non valida. */
  dataPubblicazione(iso: string | null | undefined): string {
    if (!iso) {
      return '';
    }
    const d = parseBackendLocalDateTime(iso);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return d.toLocaleDateString(this.transloco.getActiveLang() || 'it', {dateStyle: 'long'});
  }

  idSpunta(spuntaId: string): string {
    return `spunta-${spuntaId}`;
  }

  // ---- consulta -----------------------------------------------------------------------------

  caricaConsulta(): void {
    this.consultaStato.set('caricamento');
    this.annuncioConsulta.set('');
    // La regione live deve essere già nel DOM, vuota, prima che il testo compaia: altrimenti non si annuncia.
    setTimeout(() => {
      if (this.consultaStato() === 'caricamento') {
        this.annuncioConsulta.set(this.transloco.translate('documentiComune.caricamento'));
      }
    });
    this.api.vigenti$(this.slug).subscribe({
      next: (documenti) => {
        const elenco = documenti ?? [];
        this.vigenti.set(elenco);
        this.consultaStato.set('pronto');
        this.annuncioConsulta.set(
          elenco.length === 0
            ? this.transloco.translate('documentiComune.consulta.vuoto')
            : this.transloco.translate(
                `documentiComune.consulta.numeroDocumenti.${elenco.length === 1 ? 'one' : 'other'}`,
                {count: elenco.length},
              ),
        );
      },
      error: () => {
        this.consultaStato.set('errore');
        // L'esito lo annuncia già la regione role="alert".
        this.annuncioConsulta.set('');
      },
    });
  }

  /** Valore della spunta nell'ultima accettazione, `null` se non presente. */
  valoreAccettato(doc: DocumentoVigente, spuntaId: string): boolean | null {
    const trovata = doc.mia_accettazione?.spunte.find((s) => s.spunta_id === spuntaId);
    return trovata ? trovata.valore : null;
  }

  /**
   * Testo di stato del documento nella vista di lettura e nella card del gate: «Nuovo» per uno mai
   * accettato, «Aggiornato» per una nuova versione. Nella consulta il documento è già noto, quindi
   * sempre «Aggiornato».
   */
  eNuovo(doc: DocumentoCittadino): boolean {
    return this.modalita === 'gate' && !doc.aggiornamento;
  }

  chiudi(): void {
    void this.modalController.dismiss(null, DOCUMENTI_COMUNE_ESITO_CHIUDI, this.idModale);
  }
}
